import { useEffect, useRef, useState } from "react";
import { TrendingUp, TrendingDown, Zap, ShieldAlert, Waves, Brain, Target } from "lucide-react";

/* Cinematic chart-integrated HUD markers for agent-driven events (§13).
   Rendered inside the chart panel, not as generic toasts. */

const STYLE = {
  good: { col: "#00E5B5", Icon: TrendingUp },
  warn: { col: "#F5B942", Icon: Zap },
  alert: { col: "#FF3355", Icon: ShieldAlert },
  info: { col: "#00E5FF", Icon: Brain },
};
const ICONS = {
  direction_flipped: Waves,
  prognosis_invalidated: ShieldAlert,
  pme_tp1_hit: Target, pme_tp2_hit: Target, pme_tp3_hit: Target, pme_tp1_approaching: Target, pme_tp2_approaching: Target,
  pme_be: ShieldAlert, pme_runner: Waves, pme_scale_in_eligible: Brain, pme_unprotected: ShieldAlert, pme_unprotected_esc: ShieldAlert,
  prognosis_adjusted: Brain,
  sweep_imminent: Waves,
  sweep_confirmed: Waves,
  bias_strengthening: TrendingUp,
  bias_weakening: TrendingDown,
};
const LIFETIME = 12000;

export const AiHud = ({ ai, symbol }) => {
  const [live, setLive] = useState([]);
  const seen = useRef(new Set());

  useEffect(() => {
    const incoming = (ai?.notifications || []).filter((n) => n.symbol === symbol);
    const fresh = incoming.filter((n) => !seen.current.has(n.id));
    if (!fresh.length) return;
    fresh.forEach((n) => seen.current.add(n.id));
    setLive((cur) => [...fresh.map((n) => ({ ...n, shown: Date.now() })), ...cur].slice(0, 3));
  }, [ai?.notifications, symbol]);

  useEffect(() => {
    if (!live.length) return;
    const id = setInterval(() => setLive((cur) => cur.filter((n) => Date.now() - n.shown < LIFETIME)), 1000);
    return () => clearInterval(id);
  }, [live.length]);

  if (!live.length) return null;

  return (
    <div className="pointer-events-none absolute left-1/2 top-3 z-30 flex -translate-x-1/2 flex-col items-center gap-1.5"
      data-testid="ai-hud">
      {live.map((n, i) => {
        const s = STYLE[n.level] || STYLE.info;
        const Icon = ICONS[n.kind] || s.Icon;
        return (
          <div key={n.id} data-testid={`ai-hud-${n.kind}`}
            className="flex items-center gap-2 rounded-md border px-3 py-1.5 backdrop-blur-xl"
            style={{
              borderColor: `${s.col}55`, background: "rgba(6,10,16,0.82)",
              boxShadow: `0 0 26px ${s.col}33`, opacity: 1 - i * 0.28,
              animation: "aihud-in 420ms cubic-bezier(.2,.8,.2,1)",
            }}>
            <Icon size={12} style={{ color: s.col }} />
            <span className="text-[10px] font-bold font-mono tracking-[0.14em]" style={{ color: s.col }}>
              {n.title}
            </span>
            {n.detail && <span className="text-[10px] font-mono text-zinc-400">{n.detail}</span>}
            <span className="text-[8px] font-mono uppercase tracking-[0.14em] text-zinc-600">DESK</span>
          </div>
        );
      })}
      <style>{`@keyframes aihud-in{from{opacity:0;transform:translateY(-8px) scale(.97)}to{opacity:1;transform:none}}`}</style>
    </div>
  );
};
